import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import { supabase, type Tag, type TagInsert, type TagUpdate } from '../lib/supabase'

interface TagsState {
  tags: Tag[]
  isLoading: boolean
  error: string | null
  selectedTags: string[]
}

interface TagsActions {
  // Data fetching
  fetchTags: () => Promise<void>
  
  // CRUD operations
  createTag: (name: string, color?: string) => Promise<Tag | null>
  updateTag: (id: string, updates: Partial<TagUpdate>) => Promise<void>
  deleteTag: (id: string) => Promise<void>
  
  // Tag associations
  addTagToNote: (noteId: string, tagId: string) => Promise<void>
  removeTagFromNote: (noteId: string, tagId: string) => Promise<void>
  getTagsForNote: (noteId: string) => Promise<Tag[]>
  
  // UI state
  toggleTagSelection: (tagId: string) => void
  clearSelectedTags: () => void
  
  // Utility
  clearError: () => void
  updateTagNoteCounts: () => Promise<void>
}

export type TagsStore = TagsState & TagsActions

// Predefined colors for tags
const TAG_COLORS = [
  '#ef4444', // red
  '#f97316', // orange
  '#eab308', // yellow
  '#22c55e', // green
  '#06b6d4', // cyan
  '#3b82f6', // blue
  '#8b5cf6', // violet
  '#ec4899', // pink
  '#6b7280', // gray
  '#84cc16'  // lime
]

export const useTagsStore = create<TagsStore>()(
  immer((set, get) => ({
    // Initial state
    tags: [],
    isLoading: false,
    error: null,
    selectedTags: [],

    // Actions
    fetchTags: async () => {
      set((state) => {
        state.isLoading = true
        state.error = null
      })

      try {
        const { data: session } = await supabase.auth.getSession()
        if (!session.session?.user) {
          throw new Error('User not authenticated')
        }

        const { data, error } = await supabase
          .from('tags')
          .select('*')
          .eq('user_id', session.session.user.id)
          .order('name')

        if (error) throw error

        set((state) => {
          state.tags = data || []
          state.isLoading = false
        })
      } catch (error) {
        set((state) => {
          state.error = error instanceof Error ? error.message : 'Failed to fetch tags'
          state.isLoading = false
        })
      }
    },

    createTag: async (name: string, color?: string) => {
      try {
        const { data: session } = await supabase.auth.getSession()
        if (!session.session?.user) {
          throw new Error('User not authenticated')
        }

        // Check if tag already exists
        const existingTag = get().tags.find(tag => 
          tag.name.toLowerCase() === name.toLowerCase()
        )
        
        if (existingTag) {
          return existingTag
        }

        // Assign a random color if none provided
        const tagColor = color || TAG_COLORS[Math.floor(Math.random() * TAG_COLORS.length)]

        const newTag: TagInsert = {
          user_id: session.session.user.id,
          name: name.trim(),
          color: tagColor,
          note_count: 0
        }

        const { data, error } = await supabase
          .from('tags')
          .insert(newTag)
          .select()
          .single()

        if (error) throw error

        set((state) => {
          state.tags.push(data)
          state.tags.sort((a, b) => a.name.localeCompare(b.name))
        })

        return data
      } catch (error) {
        set((state) => {
          state.error = error instanceof Error ? error.message : 'Failed to create tag'
        })
        return null
      }
    },

    updateTag: async (id: string, updates: Partial<TagUpdate>) => {
      try {
        const { data, error } = await supabase
          .from('tags')
          .update(updates)
          .eq('id', id)
          .select()
          .single()

        if (error) throw error

        set((state) => {
          const tagIndex = state.tags.findIndex(tag => tag.id === id)
          if (tagIndex !== -1) {
            state.tags[tagIndex] = data
          }
        })
      } catch (error) {
        set((state) => {
          state.error = error instanceof Error ? error.message : 'Failed to update tag'
        })
      }
    },

    deleteTag: async (id: string) => {
      try {
        // First remove all note associations
        await supabase
          .from('note_tags')
          .delete()
          .eq('tag_id', id)

        // Then delete the tag
        const { error } = await supabase
          .from('tags')
          .delete()
          .eq('id', id)

        if (error) throw error

        set((state) => {
          state.tags = state.tags.filter(tag => tag.id !== id)
          state.selectedTags = state.selectedTags.filter(tagId => tagId !== id)
        })
      } catch (error) {
        set((state) => {
          state.error = error instanceof Error ? error.message : 'Failed to delete tag'
        })
      }
    },

    addTagToNote: async (noteId: string, tagId: string) => {
      try {
        // Check if association already exists
        const { data: existing, error: selectError } = await supabase
          .from('note_tags')
          .select('*')
          .eq('note_id', noteId)
          .eq('tag_id', tagId)
          .maybeSingle()

        // If there's a real error (not just "no rows"), throw it
        if (selectError && selectError.code !== 'PGRST116') {
          throw selectError
        }

        if (existing) return

        const { error } = await supabase
          .from('note_tags')
          .insert({ note_id: noteId, tag_id: tagId })

        if (error) throw error

        // Update tag note count
        await get().updateTagNoteCounts()
      } catch (error) {
        set((state) => {
          state.error = error instanceof Error ? error.message : 'Failed to add tag to note'
        })
      }
    },

    removeTagFromNote: async (noteId: string, tagId: string) => {
      try {
        const { error } = await supabase
          .from('note_tags')
          .delete()
          .eq('note_id', noteId)
          .eq('tag_id', tagId)

        if (error) throw error

        // Update tag note count
        await get().updateTagNoteCounts()
      } catch (error) {
        set((state) => {
          state.error = error instanceof Error ? error.message : 'Failed to remove tag from note'
        })
      }
    },

    getTagsForNote: async (noteId: string) => {
      try {
        const { data, error } = await supabase
          .from('note_tags')
          .select(`
            tag_id,
            tags (*)
          `)
          .eq('note_id', noteId)

        if (error) throw error

        return data?.map(item => (item as any).tags).filter(Boolean) as Tag[] || []
      } catch (error) {
        set((state) => {
          state.error = error instanceof Error ? error.message : 'Failed to fetch tags for note'
        })
        return []
      }
    },

    toggleTagSelection: (tagId: string) => {
      set((state) => {
        if (state.selectedTags.includes(tagId)) {
          state.selectedTags = state.selectedTags.filter(id => id !== tagId)
        } else {
          state.selectedTags.push(tagId)
        }
      })
    },

    clearSelectedTags: () => {
      set((state) => {
        state.selectedTags = []
      })
    },

    updateTagNoteCounts: async () => {
      try {
        const { data: session } = await supabase.auth.getSession()
        if (!session.session?.user) return

        // Get note counts for each tag
        const { data: tagCounts, error } = await supabase
          .from('note_tags')
          .select(`
            tag_id,
            notes!inner(id)
          `)
          .eq('notes.user_id', session.session.user.id)
          .eq('notes.is_trashed', false)

        if (error) throw error

        // Count notes per tag
        const countMap = new Map<string, number>()
        tagCounts?.forEach(item => {
          const current = countMap.get(item.tag_id) || 0
          countMap.set(item.tag_id, current + 1)
        })

        // Update tags with new counts
        const tags = get().tags
        for (const tag of tags) {
          const newCount = countMap.get(tag.id) || 0
          if (tag.note_count !== newCount) {
            await supabase
              .from('tags')
              .update({ note_count: newCount })
              .eq('id', tag.id)

            set((state) => {
              const tagIndex = state.tags.findIndex(t => t.id === tag.id)
              if (tagIndex !== -1) {
                state.tags[tagIndex].note_count = newCount
              }
            })
          }
        }
      } catch (error) {
        console.error('Failed to update tag note counts:', error)
      }
    },

    clearError: () => {
      set((state) => {
        state.error = null
      })
    }
  }))
)